import { type ScaleLinear, scaleLinear } from "d3";
import { is, repeat } from "ramda";

import type { Range } from "../graphql.js";
import { type INgsCoverageTrackData } from "../ui-modules/genome-viewer/ngs-track/NgsCoverageTrackLoader.js";
import binarySearch from "./binary-search.js";
import { rangeEquals } from "./range.js";

export interface IRasterSegment {
  x: number; // x-coordinate in pixels
  mid: number; // middle-coordinate in pixels
  value: number; // unscaled, max-consolidated value
  width: number; // width of the segment in pixels
}

export interface GenericProfile {
  start: number;
  stop: number;
  score: number;
}

export interface IRasterSet {
  fileName: string;
  rasterSegments: IRasterSegment[];
}

/**
 * A service that takes profile features as input and rasters the segments along the x-axis with a given resolution.
 * The return value are two arrays of x and y values. The condensing method for summarizing multiple values that fall
 * into the same x-value is taking the MAX.
 */
export class RasterizationService {
  private readonly scale: ScaleLinear<number, number>;

  constructor(scale: ScaleLinear<number, number>) {
    this.scale = scale;
  }

  rasterProfileFeatures(profileFeatures: GenericProfile[], visibleRange: Range): IRasterSegment[] {
    if (!profileFeatures?.length) {
      return [];
    }

    const raster = this.convertToRaster(profileFeatures, visibleRange);
    return this.getSegments(raster);
  }

  getSegments(raster: number[]): IRasterSegment[] {
    const segments: IRasterSegment[] = [];
    let currX: number = null;
    let currY: number = null;

    // segment raster into stretches with same value
    for (let i = 0; i < raster.length; i++) {
      if (raster[i] !== currY && is(Number, currY)) {
        // close current segment
        segments.push({
          x: currX,
          mid: (currX + i - 1) / 2,
          //  simplified from:  (currX + (currX + i - currX - 1)) / 2,
          value: currY,
          width: i - currX,
        });

        currX = null;
        currY = null;
      }

      if (currY === null && is(Number, raster[i])) {
        // start new segment
        currX = i;
        currY = raster[i];
      }
    }

    // close last segment
    if (is(Number, currY)) {
      segments.push({
        x: currX,
        value: currY,
        mid: (currX + raster.length - 1) / 2,
        //  simplified from:  (currX + (currX + raster.length - currX - 1)) / 2,
        width: raster.length - currX,
      });
    }

    return segments;
  }

  private convertToRaster(profiles: GenericProfile[], visibleRange: Range): number[] {
    // we don't want to iterate over all profile features because this is slow, find the index range instead
    const indexRange = RasterizationService.calculateIndexRange(profiles, visibleRange.start, visibleRange.stop + 1);
    const startIndex = indexRange[0];
    const endIndex = indexRange[1];

    const canvasWidth = Math.round(this.scale.range()[1]);
    const raster: number[] = repeat(0, canvasWidth);
    //  one raster entry per pixel

    for (let i = startIndex; i < endIndex; i++) {
      const { start, stop, score } = profiles[i];

      // subtract 1 because tick marks are positioned to the right of bases
      const x1 = Math.max(Math.round(this.scale(start - 1)), 0);
      // x2 is inclusive and >= x
      const x2 = Math.max(Math.min(Math.round(this.scale(stop)), canvasWidth - 1), x1);

      for (let j = x1; j <= x2; j++) {
        raster[j] = raster[j] ? Math.max(raster[j], score) : score;
        // applies the condensation function, here max(all values in pixel)
      }
    }

    return raster;
  }

  private static calculateIndexRange(
    profiles: GenericProfile[],
    rangeStart: number,
    rangeEnd: number,
  ): [number, number] {
    // calculate start index (genomicEnd is considered to be exclusive)
    let start = binarySearch(profiles, rangeStart, (a, b) => a.stop - b);

    if (start < 0) {
      // binarySearch returns index of the search key, if it is contained in the array; otherwise, (-(insertion point) - 1).
      // The insertion point is defined as the point at which the key would be inserted into the array: the index
      // of the first element greater than the key
      start = -start - 1;
    }

    // calculate end index
    let stop = binarySearch(profiles, rangeEnd, (a, b) => a.start - b);

    if (stop < 0) {
      stop = -stop - 1;
    }

    // return [Math.max(start - 1, 0), Math.min(stop + 1, profiles.starts.length)];
    return [start, stop];
  }
}

export function buildRasterSlices(data: number[][]): number[][][] {
  // build the data slices, splitting the data at y=0 values and omitting 0-only areas
  const slicedData: number[][][] = [];
  let slice: number[][] = [];

  data.forEach((d) => {
    // if we encounter a y=0 value...
    if (d[1] === 0) {
      // check if there were some non-0 data before
      if (slice.length > 0) {
        // add the zero to make sure the line goes down to the x axis again
        slice.push(d);
        // store and re-init the slice
        slicedData.push(slice);
        slice = [];
      }
    } else {
      // if this is the first data point, add an artificial 0 just before it
      if (slice.length === 0 && d[0] > 0) {
        slice.push([d[0] - 1, 0]);
      }
      slice.push(d);
    }
  });
  // deal with the last data set
  if (slice.length > 0) {
    slicedData.push(slice);
  }
  return slicedData;
}
